import type { ChangelogCategory, ChangelogEntry, ChangelogEntryText, UserActivity } from '../types'

const DAY = 24 * 60 * 60 * 1000

export const ALL_MODE_MAX_AWAY_MS = 14 * DAY
export const ALL_MODE_MAX_ITEMS = 8
export const HIGHLIGHTS_CAP = 5
export const ENTRY_WINDOW = 40
export const CATEGORY_ORDER: ChangelogCategory[] = ['planning', 'social', 'live-day', 'content', 'app']

export type DigestMode = 'all' | 'highlights'

export interface Digest {
  mode: DigestMode
  /** Every entry shown, newest first in `all` mode, by rank in `highlights`. */
  entries: ChangelogEntry[]
  /** `highlights` only: the shown entries grouped in CATEGORY_ORDER. */
  groups: { category: ChangelogCategory; entries: ChangelogEntry[] }[]
  /** Unseen entries the highlights cap left out. */
  hiddenCount: number
}

/**
 * When the user was last here before this visit. Today's activity stamp may or
 * may not have landed yet; both sides give the same answer.
 */
export function lastVisitAt(activity: UserActivity | null | undefined, now: number): number | null {
  if (!activity) return null
  const today = new Date(now).toDateString()
  if (new Date(activity.lastActiveAt).toDateString() === today) return activity.previousActiveAt ?? null
  return activity.lastActiveAt
}

export function selectDigest(entries: readonly ChangelogEntry[], { cutoff, awayMs }: { cutoff: number; awayMs: number }): Digest {
  const unseen = entries.filter(e => e.publishedAt > cutoff).sort((a, b) => b.publishedAt - a.publishedAt)
  if (awayMs < ALL_MODE_MAX_AWAY_MS && unseen.length <= ALL_MODE_MAX_ITEMS) {
    return { mode: 'all', entries: unseen, groups: [], hiddenCount: 0 }
  }
  const shown = [...unseen]
    .sort((a, b) => b.importance - a.importance || b.publishedAt - a.publishedAt)
    .slice(0, HIGHLIGHTS_CAP)
  const groups = CATEGORY_ORDER.map(category => ({ category, entries: shown.filter(e => e.category === category) })).filter(
    g => g.entries.length > 0,
  )
  return {
    mode: 'highlights',
    entries: groups.flatMap(g => g.entries),
    groups,
    hiddenCount: unseen.length - shown.length,
  }
}

/** The newest entries regardless of what was seen — the permanent Settings entry point. */
export function recentDigest(entries: readonly ChangelogEntry[]): Digest {
  const newest = [...entries].sort((a, b) => b.publishedAt - a.publishedAt).slice(0, ALL_MODE_MAX_ITEMS)
  return { mode: 'all', entries: newest, groups: [], hiddenCount: 0 }
}

export function entryText(entry: ChangelogEntry, locale: string): ChangelogEntryText {
  const localized = locale === 'es_CL' ? entry.i18n?.es_CL : undefined
  if (localized?.title && localized.body) return localized
  return { title: entry.title, body: entry.body }
}
